import { useEffect, useState } from "react";
import { Save, Check, Pencil, Link2, Mail } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { getVisitorId } from "../utils/analytics";
import sfx from "../utils/sound";
import ChunkyDatePicker from "./ChunkyDatePicker";

const BASE = process.env.REACT_APP_BACKEND_URL;

const AVATAR_CHOICES = [
  "🐻", "🐼", "🦊", "🐸", "🐵", "🐯",
  "🦁", "🐨", "🐰", "🦄", "🐙", "🐧",
];

const LANGUAGE_CHOICES = [
  { code: "", label: "Auto (browser)" },
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "hi", label: "हिन्दी" },
  { code: "ur", label: "اردو" },
  { code: "tr", label: "Türkçe" },
  { code: "pt", label: "Português" },
  { code: "ja", label: "日本語" },
  { code: "zh", label: "中文" },
];

// Profile "Edit info" card. Lets the visitor update everything sensible
// (nickname, birthday, avatar, language, notification pref). Coins are
// intentionally read-only so nobody can cheat them from the settings panel.
const EditInfoCard = () => {
  const {
    name, dob, avatar, language, notificationsOn, coins,
    setName, setDob, setAvatar, setLanguage, setNotificationsOn,
  } = useStore();

  const [n, setN] = useState(name || "");
  const [d, setD] = useState(dob || "");
  const [a, setA] = useState(avatar || "🐻");
  const [l, setL] = useState(language || "");
  const [notif, setNotif] = useState(notificationsOn !== false);
  const [saved, setSaved] = useState(false);
  const [linkedEmail, setLinkedEmail] = useState("");

  // Fetch whether this visitor already has a Google email linked. This lets
  // us show either the linked email or a "Link my Google account" CTA.
  useEffect(() => {
    let cancelled = false;
    const vid = encodeURIComponent(getVisitorId());
    fetch(`${BASE}/api/visitor/me?visitor_id=${vid}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (!cancelled && j) setLinkedEmail(j.google_email || ""); })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, []);

  const linkGoogle = () => {
    const redirect = window.location.origin + "/auth/callback";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirect);
  };

  useEffect(() => setN(name || ""), [name]);
  useEffect(() => setD(dob || ""), [dob]);
  useEffect(() => setA(avatar || "🐻"), [avatar]);
  useEffect(() => setL(language || ""), [language]);
  useEffect(() => setNotif(notificationsOn !== false), [notificationsOn]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const dirty =
    n.trim() !== (name || "") ||
    d !== (dob || "") ||
    a !== (avatar || "🐻") ||
    l !== (language || "") ||
    notif !== (notificationsOn !== false);

  const save = async (e) => {
    e?.preventDefault?.();
    const nick = n.trim();
    if (!nick) return;
    // Setters update state; the StoreContext dob/name effect pings
    // /api/analytics/visit as a single source of truth (no double POST).
    setName(nick);
    setDob(d || "");
    setAvatar(a || "🐻");
    setLanguage(l || "");
    setNotificationsOn(notif);
    sfx.pop();
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  return (
    <div
      className="chunky-card p-5 sm:p-6 mb-6"
      style={{ background: "#FFF9E6" }}
      data-testid="edit-info-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <div
          className="rounded-full flex items-center justify-center"
          style={{
            width: 40, height: 40, background: "#54A0FF",
            border: "3px solid #2b2b2b", boxShadow: "0 3px 0 #2b2b2b", color: "#fff",
          }}
        >
          <Pencil size={20} strokeWidth={3} />
        </div>
        <h2 className="font-display text-2xl leading-none">Edit your info</h2>
      </div>

      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Google account link (spans full width) */}
        <div className="sm:col-span-2" data-testid="edit-google-link-row">
          {linkedEmail ? (
            <div
              className="rounded-2xl p-3 flex items-center gap-2 text-sm"
              style={{
                border: "3px solid #2b2b2b", boxShadow: "0 3px 0 #2b2b2b",
                background: "#E9FFE6", color: "#2b2b2b",
              }}
              data-testid="edit-linked-email"
            >
              <Mail size={18} strokeWidth={3} />
              <span className="truncate">Linked to <strong>{linkedEmail}</strong></span>
            </div>
          ) : (
            <button
              type="button"
              onClick={linkGoogle}
              data-testid="edit-link-google-btn"
              className="btn-3d w-full"
              style={{ background: "#54A0FF" }}
            >
              <Link2 size={16} strokeWidth={3} />
              Link my Google account
            </button>
          )}
        </div>

        {/* Nickname */}
        <label className="text-sm font-display" style={{ color: "#484848" }}>
          <span className="block mb-1">Nickname</span>
          <input
            type="text"
            value={n}
            onChange={(e) => setN(e.target.value)}
            maxLength={20}
            placeholder="e.g. Zoe"
            data-testid="edit-name-input"
            className="block w-full rounded-2xl px-3 py-3 outline-none"
            style={{
              border: "3px solid #2b2b2b", boxShadow: "0 4px 0 #2b2b2b",
              background: "#fff", color: "#484848", fontSize: 16,
              minWidth: 0, maxWidth: "100%", boxSizing: "border-box",
            }}
          />
        </label>

        {/* Birthday */}
        <div className="text-sm font-display" style={{ color: "#484848" }}>
          <span className="block mb-1">Birthday (optional)</span>
          <ChunkyDatePicker
            value={d}
            onChange={setD}
            testIdPrefix="edit-dob"
            maxYear={new Date().getFullYear()}
          />
          <input
            type="hidden"
            data-testid="edit-dob-input"
            value={d}
            readOnly
          />
        </div>

        {/* Language */}
        <label className="text-sm font-display" style={{ color: "#484848" }}>
          <span className="block mb-1">Language</span>
          <select
            value={l}
            onChange={(e) => setL(e.target.value)}
            data-testid="edit-language-select"
            className="block w-full rounded-2xl px-3 py-3 outline-none"
            style={{
              border: "3px solid #2b2b2b", boxShadow: "0 4px 0 #2b2b2b",
              background: "#fff", color: "#484848", fontSize: 16,
              minWidth: 0, maxWidth: "100%", boxSizing: "border-box",
            }}
          >
            {LANGUAGE_CHOICES.map((opt) => (
              <option key={opt.code || "auto"} value={opt.code}>{opt.label}</option>
            ))}
          </select>
        </label>

        {/* Coins (read-only) */}
        <label className="text-sm font-display" style={{ color: "#484848" }}>
          <span className="block mb-1">Coins (read-only)</span>
          <div
            className="block w-full rounded-2xl px-3 py-3"
            data-testid="edit-coins-readonly"
            style={{
              border: "3px solid #2b2b2b", boxShadow: "0 4px 0 #2b2b2b",
              background: "#FFF3B0", color: "#8A5A00", fontSize: 16,
              boxSizing: "border-box",
            }}
          >
            🪙 {coins}
          </div>
        </label>

        {/* Avatar picker */}
        <div className="sm:col-span-2 text-sm font-display" style={{ color: "#484848" }}>
          <span className="block mb-2">Avatar</span>
          <div className="grid grid-cols-6 sm:grid-cols-12 gap-2" data-testid="edit-avatar-grid">
            {AVATAR_CHOICES.map((emo) => (
              <button
                key={emo}
                type="button"
                onClick={() => setA(emo)}
                aria-label={`Avatar ${emo}`}
                aria-pressed={a === emo}
                data-testid={`edit-avatar-${emo}`}
                className="rounded-2xl flex items-center justify-center transition-transform hover:-translate-y-0.5"
                style={{
                  width: "100%", aspectRatio: "1 / 1", fontSize: 26,
                  background: a === emo ? "#54D62C" : "#fff",
                  border: "3px solid #2b2b2b",
                  boxShadow: a === emo ? "0 5px 0 #2b2b2b" : "0 3px 0 #2b2b2b",
                }}
              >
                {emo}
              </button>
            ))}
          </div>
        </div>

        {/* Notifications */}
        <label
          className="sm:col-span-2 flex items-center gap-3 text-sm font-display cursor-pointer"
          style={{ color: "#484848" }}
        >
          <input
            type="checkbox"
            checked={notif}
            onChange={(e) => setNotif(e.target.checked)}
            data-testid="edit-notifications-toggle"
            style={{ width: 22, height: 22 }}
          />
          Send me sticker &amp; streak notifications
        </label>

        <div className="sm:col-span-2 flex items-center gap-3">
          <button
            type="submit"
            disabled={!dirty || !n.trim()}
            data-testid="edit-save-btn"
            className="btn-3d"
            style={{ fontSize: 18 }}
          >
            {saved ? (
              <>
                <Check size={18} strokeWidth={3} /> Saved!
              </>
            ) : (
              <>
                <Save size={18} strokeWidth={3} /> Save changes
              </>
            )}
          </button>
          {saved && (
            <span
              data-testid="edit-save-toast"
              className="text-sm font-display"
              style={{ color: "#54D62C" }}
            >
              Your info is updated.
            </span>
          )}
        </div>
      </form>
    </div>
  );
};

export default EditInfoCard;
